import type { ColumnDef } from "@tanstack/react-table";

import type { BaseRowItem } from "../../components/data-table";
import type { AdminPayoutTransaction } from "../../lib/api/adminTransactionsData";
import { formatAmount } from "../../utils/FormatPerson";
import { statusBadgeClass } from "../orders/ordersOrderTable";

export type PayoutTableRow = BaseRowItem & {
  raw: AdminPayoutTransaction;
  displayDate: string;
  userName: string;
  assetLabel: string;
  usdLabel: string;
  ngnLabel: string;
  bankLabel: string;
  accountLine: string;
  statusLabel: string;
  providerLabel: string;
  reference: string;
};

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function num(v: unknown): number | undefined {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function recipient(tx: AdminPayoutTransaction): Record<string, unknown> | null {
  const r = tx.recipient;
  if (r && typeof r === "object" && !Array.isArray(r)) {
    return r as Record<string, unknown>;
  }
  return null;
}

/**
 * Prefer API `recipient.bankName`; if missing, resolve via master bank list by `bankCode`
 * (legacy giftcard payouts used `banks.filter((b) => b.code === bankCode).map((b) => b.name)`).
 */
export function getPayoutBankDisplayName(
  tx: AdminPayoutTransaction,
  banksByCode: Map<string, string>
): string {
  const rec = recipient(tx);
  const fromApi = str(rec?.bankName).trim();
  if (fromApi) return fromApi;
  const code = str(rec?.bankCode).trim();
  if (!code) return "—";
  return banksByCode.get(code) ?? code;
}

export function getPayoutDisplayDate(tx: AdminPayoutTransaction): string {
  const iso = str(tx.createdAt) || str(tx.updatedAt);
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function userDisplayName(tx: AdminPayoutTransaction): string {
  const u = tx.user;
  if (!u || typeof u !== "object") return "—";
  const profile = (u as { profile?: { firstName?: string; lastName?: string } })
    .profile;
  const f = profile?.firstName?.trim() ?? "";
  const l = profile?.lastName?.trim() ?? "";
  const name = `${f} ${l}`.trim();
  return name || "—";
}

function assetName(tx: AdminPayoutTransaction): string {
  const a = tx.asset;
  if (!a || typeof a !== "object") return "—";
  return str((a as { name?: unknown }).name) || "—";
}

export function buildPayoutTableRows(
  transactions: AdminPayoutTransaction[],
  banksByCode: Map<string, string>
): PayoutTableRow[] {
  return transactions.map((tx) => {
    const rec = recipient(tx);
    const acct = str(rec?.accountNumber);
    const acctName = str(rec?.accountName);
    const accountLine =
      acct || acctName
        ? [acctName, acct].filter(Boolean).join(" · ")
        : "—";

    const usd = num(
      tx.asset && typeof tx.asset === "object"
        ? (tx.asset as { amountInUSD?: unknown }).amountInUSD
        : undefined
    );
    const ngn = num(tx.payoutAmount);

    return {
      id: tx._id,
      raw: tx,
      displayDate: getPayoutDisplayDate(tx),
      userName: userDisplayName(tx),
      assetLabel: assetName(tx),
      usdLabel: usd != null ? `$${formatAmount(usd)}` : "—",
      ngnLabel: ngn != null ? `₦${formatAmount(ngn)}` : "—",
      bankLabel: getPayoutBankDisplayName(tx, banksByCode),
      accountLine,
      statusLabel: str(tx.status) || "—",
      providerLabel: str(tx.provider) || "—",
      reference: str(tx.reference) || "—",
    };
  });
}

export function createPayoutTableColumns(
  onView: (row: PayoutTableRow) => void
): ColumnDef<PayoutTableRow, unknown>[] {
  return [
    {
      accessorKey: "displayDate",
      header: "Date",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-adminTable-muted">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "userName",
      header: "User",
      meta: { hideBelow: "sm" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-white">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "assetLabel",
      header: "Asset",
      cell: ({ getValue }) => (
        <span className="text-[12px] font-semibold uppercase text-[#BEE1FB]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "usdLabel",
      header: "USD",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="tabular-nums text-[13px] text-[#E7EBEE]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "ngnLabel",
      header: "NGN",
      cell: ({ getValue }) => (
        <span className="tabular-nums text-[13px] font-medium text-white">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "bankLabel",
      header: "Bank",
      meta: { wrap: true, maxWidth: "200px" },
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className="text-[13px] text-white leading-snug">
            {row.original.bankLabel}
          </p>
          <p className="text-[11px] text-[#869AA9] mt-0.5 truncate">
            {row.original.accountLine}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "statusLabel",
      header: "Status",
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${statusBadgeClass(
            row.original.raw.status
          )}`}
        >
          {row.original.statusLabel}
        </span>
      ),
    },
    {
      accessorKey: "providerLabel",
      header: "Provider",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="text-[12px] text-[#AEBCC6]">{String(getValue())}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { align: "right" as const },
      cell: ({ row }) => (
        <button
          type="button"
          className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-secondary_button hover:border-secondary_button hover:bg-[#273037]"
          onClick={() => onView(row.original)}
        >
          View
        </button>
      ),
    },
  ];
}
